

import { Search, Plus, Filter, X, Upload } from 'lucide-react';

import { SOURCES, STATUSES } from './contactListTypes';
import { useContactsController } from './useContactsController';
export function ContactListControls({ ctx }: { ctx: ReturnType<typeof useContactsController> }) {
const { setPage, total, search, setSearch, filterStatus, setFilterStatus, filterSource, setFilterSource, filterCustomerType, setFilterCustomerType, showFilters, setShowFilters, setShowModal, setShowImportModal, setImportFile, setImportPreview, setImportAllRows, setImportResult, summaryStats } = ctx;
return (<>
      {/* Header — Apr 23, 2026 (Agent 4 / Fix B): grid with auto-column for the
          action buttons + truncate on the title so the buttons are ALWAYS on
          screen regardless of viewport width or sidebar/AI panel state.
          `flex-wrap` alone was insufficient at 1512px — the long subtitle +
          title column was still eating enough horizontal space to clip the
          Add Contact button. Grid with min-w-0 on the title column forces the
          title to truncate before the buttons overflow. */}
      <div className="mb-6 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="truncate text-sm text-gray-500">Manage your contacts and leads</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => { setShowImportModal(true); setImportFile(null); setImportPreview([]); setImportAllRows([]); setImportResult(null); }}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Upload size={16} />
            Import CSV
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
          >
            <Plus size={16} />
            Add Contact
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Total Contacts</p>
          <p className="mt-1 text-lg font-bold text-gray-900">{summaryStats.total}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">New This Month</p>
          <p className="mt-1 text-lg font-bold text-[var(--ink-0)]">{summaryStats.newThisMonth}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Top Sources</p>
          <div className="mt-1">
            {summaryStats.topSources.length > 0 ? summaryStats.topSources.map(([src, count]) => (
              <span key={src} className="mr-2 inline-flex items-center text-xs text-gray-700">
                {src} <span className="ml-1 font-medium text-[var(--ink-0)]">({count})</span>
              </span>
            )) : <span className="text-xs text-gray-400">No data</span>}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Avg Lead Score</p>
          <p className="mt-1 text-lg font-bold text-gray-900">{summaryStats.avgLeadScore}</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <select
            value={filterSource}
            onChange={(e) => { setFilterSource(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Sources</option>
            {SOURCES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={filterCustomerType}
            onChange={(e) => { setFilterCustomerType(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Types</option>
            <option value="b2b">B2B</option>
            <option value="b2c">B2C</option>
          </select>
          {(filterStatus || filterSource || filterCustomerType) && (
            <button
              onClick={() => { setFilterStatus(''); setFilterSource(''); setFilterCustomerType(''); setPage(1); }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      </>);
}
